/**
 * FC client. Same policy as lmcc-exam's web client: 8 s total deadline per
 * call, automatic backoff on 429/503 and transient network failures.
 */
interface PublicConfig {
  apiBaseUrl: string;
}

const REQUEST_TIMEOUT_MS = 8_000;
const RETRYABLE_STATUS_CODES = new Set([429, 503]);
const RETRY_DELAYS_MS = [400, 900, 1_800];

export class RequestError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message);
  }
}

let configPromise: Promise<PublicConfig> | undefined;

async function loadConfig(): Promise<PublicConfig> {
  if (!configPromise) {
    configPromise = fetch("./config.json", { cache: "no-store" }).then(async (response) => {
      if (!response.ok) throw new Error("无法加载 config.json");
      const config = await response.json() as Partial<PublicConfig>;
      if (typeof config.apiBaseUrl !== "string") throw new Error("config.json 缺少 apiBaseUrl");
      return { apiBaseUrl: config.apiBaseUrl };
    }).catch((error: unknown) => {
      configPromise = undefined;
      throw error;
    });
  }
  return configPromise;
}

function delay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error("请求已取消"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    function abort(): void {
      clearTimeout(timer);
      reject(new Error("请求已取消"));
    }
    signal.addEventListener("abort", abort, { once: true });
  });
}

async function errorFromResponse(response: Response): Promise<RequestError> {
  try {
    const body = await response.json() as { error?: { code?: string; message?: string } };
    return new RequestError(
      response.status,
      body.error?.code ?? "UNKNOWN",
      body.error?.message || `请求失败（${response.status}）`
    );
  } catch {
    return new RequestError(response.status, "UNKNOWN", `请求失败（${response.status}）`);
  }
}

export async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const { apiBaseUrl } = await loadConfig();
  const url = new URL(path.replace(/^\//, ""), apiBaseUrl.replace(/\/?$/, "/")).toString();
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    let attempt = 0;
    while (true) {
      let response: Response;
      try {
        response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(body ?? {}),
          signal: controller.signal
        });
      } catch (error) {
        if (controller.signal.aborted || attempt >= RETRY_DELAYS_MS.length) throw error;
        await delay(RETRY_DELAYS_MS[attempt++], controller.signal);
        continue;
      }
      if (RETRYABLE_STATUS_CODES.has(response.status) && attempt < RETRY_DELAYS_MS.length) {
        await response.body?.cancel();
        await delay(RETRY_DELAYS_MS[attempt++], controller.signal);
        continue;
      }
      if (!response.ok) throw await errorFromResponse(response);
      return await response.json() as T;
    }
  } catch (error) {
    if (timedOut) throw new Error("请求超时，请稍后重试");
    if (error instanceof RequestError) throw error;
    throw new Error("无法连接服务器，请检查网络后重试");
  } finally {
    clearTimeout(timer);
  }
}

/** POST that returns a file; the filename comes from Content-Disposition when the gateway exposes it. */
export async function postFile(path: string, body: unknown, token: string, fallbackName: string): Promise<{ blob: Blob; filename: string }> {
  const { apiBaseUrl } = await loadConfig();
  const url = new URL(path.replace(/^\//, ""), apiBaseUrl.replace(/\/?$/, "/")).toString();
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body ?? {})
  });
  if (!response.ok) throw await errorFromResponse(response);
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  return { blob: await response.blob(), filename: match ? decodeURIComponent(match[1]) : fallbackName };
}
